import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import RegisterApi from '../api/registerApi';

/**
 * Query: Fetch sign in location by ID
 */
export const useSignInLocation = (id) => {
  return useQuery({
    queryKey: ['signInLocation', id],
    queryFn: async () => {
      const response = await RegisterApi.getSignInLocation(id);
      // API returns an array, get first element
      return response.data?.[0] || response.data;
    },
    enabled: id > 0, // Only run if ID exists
  });
};

/**
 * Query: Fetch ALL sign in locations (client-side filtering for small dataset)
 */
export const useSignInLocations = () => {
  return useQuery({
    queryKey: ['signInLocations'],
    queryFn: async () => {
      const response = await RegisterApi.getLocation(true); // true = includeAll
      return response.data || [];
    },
    staleTime: 1000 * 60 * 10, // 10 minutes - longer for small datasets
    // Uses global gcTime: 10 min
  });
};

/**
 * Mutation: Save sign in location (create or update)
 */
export const useSaveSignInLocation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (signInLocationObj) => {
      const response = await RegisterApi.saveSignInLocation(signInLocationObj);
      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate and refetch related queries
      queryClient.invalidateQueries({ queryKey: ['signInLocations'] });
      if (data && data.registerLocationId) {
        queryClient.invalidateQueries({ queryKey: ['signInLocation', data.registerLocationId] });
      }
    },
  });
};

/**
 * Returns a function that invalidates all signInLocation queries so the admin
 * module refetches fresh data (e.g., after siteBriefing was edited elsewhere).
 */
export const useInvalidateSignInLocations = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ['signInLocations'] });
    queryClient.invalidateQueries({ queryKey: ['signInLocation'] });
  };
};

/**
 * Mutation: Delete sign in location
 */
export const useDeleteSignInLocation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id) => {
      await RegisterApi.deleteSignInLocation(id);
      return id;
    },
    onSuccess: () => {
      // Invalidate the list to refetch
      queryClient.invalidateQueries({ queryKey: ['signInLocations'] });
    },
  });
};
